<template>
  <component :is="to ? NuxtLink : 'div'" :to="to" :class="$style['product']" @mouseenter="showSecond = !!product?.images?.[1]">
    <!-- The name is right under the photo, so the photo itself needs no text -->
    <div :class="[$style['product__image'], pending && $style['product__image--pending']]">
      <img
        v-if="product?.images?.length"
        ref="image"
        :src="`${config.public.cdnUrl}/products/medium/${product.images[0]}.webp`"
        alt=""
        loading="lazy"
        @load="pending = false"
        @error="pending = false">
      <!-- No photo yet: the logo, quietly, so the frame doesn't look broken -->
      <SiteMark v-else :class="$style['product__placeholder']" />
      <!-- With a mouse, the second photo shows on hover; it only loads once you point at the card -->
      <img
        v-if="showSecond"
        :src="`${config.public.cdnUrl}/products/medium/${product.images![1]}.webp`"
        alt=""
        :class="$style['product__second']"
        @load="($event.target as HTMLElement).dataset.loaded = ''">
    </div>
    <span :class="$style['product__title']">{{ product?.title }}</span>
    <div v-if="$slots.actions" :class="$style['product__actions']">
      <slot name="actions" />
    </div>
  </component>
</template>
<script setup lang="ts">
// A product in a list: the photo filling its frame, the name under it in the body font,
// at most two lines. Buttons (the admin) go in the actions slot.
defineProps<{
  product: { title?: string, images?: string[] }
  to?: string | object
}>()
const NuxtLink = resolveComponent('NuxtLink')
const config = useRuntimeConfig()
const nuxtApp = useNuxtApp()

// Photos that arrive later (more items, other filters) fade in instead of popping up.
// Not the ones from the server: those show as soon as they load, also before the page runs.
const image = ref<HTMLImageElement>()
const pending = ref(false)
const showSecond = ref(false)
onMounted(() => {
  if (!nuxtApp.isHydrating && image.value && !image.value.complete) { pending.value = true }
})
</script>
<style module>
.product {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-m);
  min-width: 0;
  color: var(--text-base);
  text-decoration: none;
}

.product__image {
  position: relative;
  display: grid;
  overflow: hidden;
  place-items: center;
  aspect-ratio: 9 / 16;
  border-radius: var(--border-radius);
  background: var(--background-raised);
}

.product__placeholder {
  font-size: 3rem;
  opacity: 0.18;
}

.product__image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 600ms var(--stage-ease, ease), opacity 400ms ease-out;
}

.product__image--pending img {
  opacity: 0;
}

.product__second {
  position: absolute;
  inset: 0;
  opacity: 0;
}

@media (hover: hover) {
  .product[href]:hover .product__second[data-loaded] {
    opacity: 1;
  }
}

.product__title {
  display: -webkit-box;
  overflow: hidden;
  font-family: var(--font-family);
  font-weight: var(--font-weight-normal);
  font-size: var(--font-size-base);
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.product__actions {
  display: flex;
  gap: var(--spacing);
}

/* A link: the photo leans in and the name turns amber */
.product[href]:hover .product__image img {
  transform: scale(1.04);
}

.product[href]:hover .product__title {
  color: var(--primary-color);
}

.product[href]:focus-visible {
  outline: none;
}

.product[href]:focus-visible .product__image {
  outline: 0.125rem solid var(--text-base);
  outline-offset: 0.125rem;
}

@media (prefers-reduced-motion: reduce) {
  .product__image img {
    transition: none;
  }
}
</style>
